// MULTISITIO (Diego, 13-oct, segunda tanda): «Crazy Motors tiene 2 sucursales… ¿dónde pongo al inicio que van a ser varios
// proyectos?». Cada sucursal es su propia cotización (sus recibos, su tarifa, sus opciones; el motor la calcula como
// siempre) y todas comparten un grupo. Esta franja va arriba de la cotización: dice qué sucursal es, lleva a las otras del
// mismo proyecto, deja agregar otra y saca el PDF de TODAS en una sola propuesta (formato multisitio).
import { useEffect, useMemo, useRef, useState } from 'react';
import { Building2, FileDown, Plus, X } from 'lucide-react';
import { AVISO_PDF_MULTISITIO, ETIQUETA_ESTADO, SITIOS_MAX, enPapelera } from '@cotizador/compartido';
import type { Cotizacion, Cuestionario, ResumenCotizacion, ResumenVersion, Tarifa } from '@cotizador/compartido';
import { api } from '../../api/consultas.ts';
import { ErrorDeApi, mensajeDe } from '../../api/cliente.ts';
import { Enlace, navegar } from '../../ruteo.tsx';
import { Boton, CampoSelect, ErrorEnLinea } from '../../componentes/ui.tsx';
import { descargarComo } from '../../componentes/archivos.tsx';
import { formaDesde } from '../../logica/cuestionario.ts';
import { detalleLegible } from '../../logica/ediciones.ts';
import { mostrarLaSeccion } from '../../logica/llegada.ts';
import { ETIQUETA_TIPO_SITIO, TIPOS_SITIO, cuestionarioDeSitio, diferenciaSitio, igualAlPrincipal, opcionesTarifa, rojoDeTodas, sitiosPorArreglar, unirFrases } from '../../logica/sitios.ts';
import type { TipoSitio } from '../../logica/sitios.ts';
import { SitiosDelProyecto } from './SitiosDelProyecto.tsx';
import { usarCotizacion } from './contexto.tsx';

/** El último PDF de todas las sucursales (formato multisitio, o consolidado si el proyecto trae casas o bimestrales: ADENDA z68-3)
 *  entre las versiones de una cotización. */
export function ultimoDeTodas(versiones: readonly ResumenVersion[]): ResumenVersion | null {
  return [...versiones].filter((v) => v.pdf?.formato === 'multisitio' || v.pdf?.formato === 'consolidado').sort((a, b) => b.numero - a.numero)[0] ?? null;
}

/** Dónde acaba lo que se ve: el borde de arriba de la barra de abajo del celular, o la ventana si no está (en la computadora va oculta). */
function bordeLibreAbajo(): number {
  const r = document.querySelector('.barra-abajo')?.getBoundingClientRect();
  return r && r.height > 0 ? Math.min(r.top, window.innerHeight) : window.innerHeight;
}
const AIRE_SOBRE_LA_BARRA = 8;

/** Una sucursal cancelada o perdida no entra al PDF de todas (el servidor la deja fuera). */
const cerrada = (c: Pick<ResumenCotizacion, 'estado'>) => c.estado === 'cancelada' || c.estado === 'perdida';

/** El rojo del PDF de todas: cada falla una vez, y debajo de ella «Ir a arreglar» de cada sitio, que parte renglón y nombra el sitio completo. */
function RojoDeTodas({ rojo, alIr, alCerrar }: { rojo: ReturnType<typeof rojoDeTodas>; alIr: (ruta: string) => void; alCerrar: () => void }) {
  const unaSola = rojo.grupos.length === 1;
  return (
    <div className="error-linea" role="alert">
      <div className="error-linea__cuerpo">
        {!unaSola && rojo.inicio && <p className="error-linea__mensaje">{rojo.inicio.trim()}</p>}
        {rojo.grupos.map((g) => (
          <div key={g.frase} className="sitios-proyecto__falla">
            <p className="error-linea__mensaje">{unirFrases(unaSola ? rojo.inicio : '', detalleLegible(g.frase))}</p>
            {g.sitios.length > 0 && (
              <div className="sitios-proyecto__arreglar">
                {g.sitios.map((x) => (
                  <Boton key={x.id} chico variante="secundario" className="boton--envuelve" onClick={() => alIr(x.ruta)}>
                    Ir a arreglar «{x.nombre}»
                  </Boton>
                ))}
              </div>
            )}
            {g.detalle.length > 0 && (
              <details>
                <summary>Ver qué cambió en cada una</summary>
                <ul className="error-linea__detalles">
                  {g.detalle.map((d) => (
                    <li key={d}>{detalleLegible(d)}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        ))}
        {rojo.cola && <p className="error-linea__mensaje">{rojo.cola}</p>}
      </div>
      <button type="button" className="icono-boton" aria-label="Cerrar el error" onClick={alCerrar}>
        <X aria-hidden />
      </button>
    </div>
  );
}

export function SucursalesProyecto({ cot, editable }: { cot: Cotizacion; editable: boolean }) {
  const ms = cot.multisitio;
  const { detalle, recargar } = usarCotizacion();
  const [otras, setOtras] = useState<ResumenCotizacion[]>([]);
  const [nombre, setNombre] = useState('');
  /** z65: el tipo y la tarifa de la sucursal nueva (null = igual que esta). */
  const [tipoNuevo, setTipoNuevo] = useState<TipoSitio | null>(null);
  const [tarifaNueva, setTarifaNueva] = useState<Tarifa | 'no_se'>('no_se');
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  /** Lo que truena al sacar el PDF de todas: va junto a sus botones (ver abajo), no abajo de la lista como el de «Agregar sucursal». */
  const [errorPdf, setErrorPdf] = useState<unknown>(null);
  const avisosTodo = useRef<HTMLDivElement>(null);
  /** Si no se pudieron cargar las sucursales (aparte del error de agregar o del PDF: uno no tapa ni borra al otro). */
  const [errorSitios, setErrorSitios] = useState<unknown>(null);
  const [haciendoPdf, setHaciendoPdf] = useState(false);
  const [pdfTodas, setPdfTodas] = useState<ResumenVersion | null>(null);
  /** Lo que quedó fuera del último PDF de todas que se sacó aquí (cuál y por qué; el servidor lo dice). */
  const [fuera, setFuera] = useState<string[]>([]);
  /** Verificador del 2-nov (z68-1): las ciudades donde el último PDF de todas cobró el viaje de cada sucursal porque una trae precio a mano (el servidor dice cuál y qué hacer). */
  const [viajeSinAplicar, setViajeSinAplicar] = useState<string[]>([]);
  /** El último PDF de todas guardado en la sucursal principal (cuando la principal es otra): sigue descargable al navegar. */
  const [dePrincipal, setDePrincipal] = useState<ResumenVersion | null>(null);
  const [vuelta, setVuelta] = useState(0);
  const grupo = ms?.grupo_id ?? null;

  // Las sucursales salen de su propia ruta (GET /api/cotizaciones/:id/sucursales), no de la lista del CRM: con el fusible
  // de listas saltado (nube) la franja se quedaba vacía y callada (hallazgo del 14-oct). Si aun así falla, se DICE.
  useEffect(() => {
    if (!grupo) return;
    let vivo = true;
    api
      .sucursales(cot.id)
      .then((r) => {
        if (!vivo) return;
        setOtras(r.cotizaciones.filter((c) => c.multisitio?.grupo_id === grupo && !enPapelera(c)));
        setErrorSitios(null);
      })
      .catch((e: unknown) => {
        if (!vivo) return;
        setOtras([]);
        setErrorSitios(e);
      });
    return () => {
      vivo = false;
    };
  }, [grupo, cot.id, vuelta]);

  // Verificador del 1-nov, medido contra producción: al subir los botones (z67) el rojo se quedó abajo de TODA la lista (a 375 con 2
  // sitios, botón en y=527 y rojo en y=1060 de 812; con 15, y=2298). El botón dejaba de girar y el vendedor no veía nada: justo los
  // candados nuevos («Saca otra vez el PDF de X», «si no cabe, que truene») se volvían fallos silenciosos. Ahora el rojo y lo que quedó
  // fuera van junto a los botones y, si aun así no caben en la pantalla, se traen a la vista (el principio primero si es más alto que ella).
  useEffect(() => {
    const el = avisosTodo.current;
    if (!el || typeof el.scrollIntoView !== 'function') return;
    // Verificador del 2-nov, medido a 390 × 844 con 2 sitios y 1 falla: el rojo acababa dentro de los 64 px de la barra de abajo, aquí no se movía nada y la barra tapaba «Ir a
    // arreglar». Lo que cuenta es lo que queda libre arriba de la barra (bordeLibreAbajo), no el alto de la ventana.
    const r = el.getBoundingClientRect();
    const libre = bordeLibreAbajo();
    if (r.height > libre) el.scrollIntoView({ block: 'start' });
    else if (r.bottom > libre) window.scrollBy({ top: r.bottom - libre + AIRE_SOBRE_LA_BARRA, behavior: 'auto' });
    else if (r.top < 0) el.scrollIntoView({ block: 'nearest' });
  }, [errorPdf, fuera, viajeSinAplicar]);
  // Un rojo es de la sucursal desde la que se pidió: al pasar a otra (también con «Ir a arreglar») no se queda pegado.
  useEffect(() => {
    setErrorPdf(null);
    setFuera([]);
    setViajeSinAplicar([]);
  }, [cot.id]);

  const sitios = useMemo(() => [...otras].sort((a, b) => a.folio.localeCompare(b.folio)), [otras]);
  // La versión del PDF de todas queda en la sucursal viva de folio más bajo (la principal): como el PDF individual, se puede
  // volver a bajar después de navegar (antes solo existía mientras no se saliera de la pantalla).
  const principal = sitios.find((c) => !cerrada(c)) ?? null;
  const propio = useMemo(() => ultimoDeTodas(detalle.versiones), [detalle.versiones]);
  useEffect(() => {
    if (!principal || principal.id === cot.id) return;
    let vivo = true;
    api
      .detalle(principal.id)
      .then((d) => {
        if (vivo) setDePrincipal(ultimoDeTodas(d.versiones));
      })
      .catch(() => {
        if (vivo) setDePrincipal(null);
      });
    return () => {
      vivo = false;
    };
  }, [principal?.id, cot.id, vuelta]);

  if (!ms) return null;
  const total = Math.max(sitios.length, 1);
  const candidatos = [pdfTodas, principal?.id === cot.id ? propio : dePrincipal].filter((v): v is ResumenVersion => !!v?.pdf);
  const mostrado = candidatos.sort((a, b) => (b.pdf!.generado_en > a.pdf!.generado_en ? 1 : -1))[0] ?? null;

  async function agregar() {
    const sitio = nombre.trim();
    if (sitio.length < 2) {
      // Diego, 25-oct: el rojo «Escribe el nombre de la sucursal» salía arriba sin decir CUÁL (parecía que «${ms.sitio}» o
      // la otra sucursal estaban mal). Es la sucursal NUEVA: la que se iba a agregar con el campo vacío.
      setError(new Error(`Para agregar otra sucursal al proyecto, escribe su nombre en «Otra sucursal» (2 a 40 letras) y vuelve a tocar «Agregar sucursal». ${sitios.length > 1 ? 'Las sucursales que ya están no cambian' : `«${ms?.sitio ?? 'Esta sucursal'}» no cambia`}.`));
      return;
    }
    setCreando(true);
    setError(null);
    try {
      // La sucursal nueva nace con el mismo cuestionario (cliente, vendedor); z65: con SU tipo y SU tarifa si se eligieron.
      const forma = formaDesde(cot.borrador.cuestionario);
      const pedido = { nombre: sitio, tipo: tipoNuevo, tarifa: tipoNuevo ? tarifaNueva : null };
      let cuestionario: Cuestionario = cot.borrador.cuestionario;
      if (!igualAlPrincipal(forma, pedido)) {
        const q = cuestionarioDeSitio(forma, pedido);
        if ('error' in q) {
          setError(new Error(`No se agregó «${sitio}»: ${q.error}`));
          return;
        }
        cuestionario = q.cuestionario;
      }
      const r = await api.crearCotizacion(cuestionario, cot.vendedor_id, { sitio, junto_a: cot.id });
      // z65 · candado 1: lo que guardó el servidor contra lo que se pidió; si no coincide se dice y no se navega callado.
      const d = diferenciaSitio(cuestionario, r.cotizacion.borrador.cuestionario);
      if (d) {
        setVuelta((n) => n + 1);
        setError(new Error(`Se agregó «${sitio}» (${r.cotizacion.folio}), pero ${d}: ábrela con «Editar» y revisa su cuestionario.`));
        return;
      }
      setNombre('');
      setTipoNuevo(null);
      navegar(`/cotizaciones/${r.cotizacion.id}/recibos`);
    } catch (e) {
      setError(e);
    } finally {
      setCreando(false);
    }
  }

  async function pdfDeTodas() {
    setHaciendoPdf(true);
    setErrorPdf(null);
    setPdfTodas(null);
    setFuera([]);
    setViajeSinAplicar([]);
    try {
      const r = await api.generarPdfSucursales(cot.id);
      setPdfTodas(r.version);
      setFuera(r.fuera_del_pdf ?? []);
      setViajeSinAplicar(r.viaje_sin_aplicar ?? []);
      // El servidor anotó los avisos y el historial de CADA sucursal (también de esta): sin recargar, la siguiente acción
      // en esta pantalla chocaba con un 409 «alguien cambió la cotización» (verificación adversarial del 13-oct, noche).
      setVuelta((n) => n + 1);
      await recargar();
    } catch (e) {
      setErrorPdf(e);
    } finally {
      setHaciendoPdf(false);
    }
  }

  // Si ya está en esa sección (el rojo es de este mismo sitio), navegar no mueve nada: aquí se trae a la vista y, si pide una forma, se abre.
  const irAArreglar = (ruta: string) => {
    const misma = ruta.split('?')[0] === window.location.pathname;
    navegar(ruta, misma ? { reemplazar: true } : { aLaSeccion: true });
    if (misma) mostrarLaSeccion();
  };
  // Un conflicto (otra persona guardó) no nombra sitios y sus detalles son de diagnóstico: va como todo conflicto.
  const rojo = errorPdf && !(errorPdf instanceof ErrorDeApi && errorPdf.codigo === 'CONFLICTO') ? rojoDeTodas(mensajeDe(errorPdf), errorPdf instanceof ErrorDeApi ? errorPdf.detalles : [], sitios) : null;

  return (
    <div className="franja-solo-ver franja-sitios" role="note" aria-label="Proyecto de varios sitios">
      <Building2 aria-hidden />
      <div className="franja-sitios__cuerpo">
        <p>
          Sucursal <strong>«{ms.sitio}»</strong> · proyecto de {total} {total === 1 ? 'sitio' : 'sitios'} de {cot.cliente_nombre}.
        </p>
        {/* Verificador del 30-oct: lo demás de la franja va dentro de «Sitios del proyecto», que en el celular nace plegada. */}
        <SitiosDelProyecto
          sitios={sitios}
          actualId={cot.id}
          vuelta={vuelta}
          todoEnUno={
            (editable && sitios.length > 1) || mostrado?.pdf || errorPdf ? (
              <>
                {mostrado?.pdf && (
                  <Boton chico variante="principal" icono={<FileDown aria-hidden />} onClick={() => void descargarComo(mostrado.pdf!.archivos.pdf, `${mostrado.pdf!.folio_pdf}-sucursales.pdf`)}>
                    Bajar todo en uno {mostrado.pdf.folio_pdf}
                  </Boton>
                )}
                {editable && sitios.length > 1 && (
                  <Boton chico variante="secundario" icono={<FileDown aria-hidden />} cargando={haciendoPdf} onClick={() => void pdfDeTodas()}>
                    PDF de todas las sucursales
                  </Boton>
                )}
                {(!!errorPdf || fuera.length > 0 || viajeSinAplicar.length > 0) && (
                  <div ref={avisosTodo} className="sitios-proyecto__avisos">
                    {rojo ? <RojoDeTodas rojo={rojo} alIr={irAArreglar} alCerrar={() => setErrorPdf(null)} /> : errorPdf ? <ErrorEnLinea error={errorPdf} alCerrar={() => setErrorPdf(null)} /> : null}
                    {viajeSinAplicar.map((f) => (
                      <div key={f} className="sitios-proyecto__falla" role="status" aria-label="Un viaje por ciudad sin aplicar">
                        <p>{f}</p>
                        <div className="sitios-proyecto__arreglar">
                          {sitiosPorArreglar([f], sitios).map((x) => (
                            <Boton key={x.id} chico variante="secundario" className="boton--envuelve" onClick={() => irAArreglar(x.ruta)}>
                              Ir a arreglar «{x.nombre}»
                            </Boton>
                          ))}
                        </div>
                      </div>
                    ))}
                    {fuera.length > 0 && (
                      <ul className="franja-sitios__lista" aria-label="Sucursales que quedaron fuera del PDF de todas">
                        {fuera.map((f) => (
                          <li key={f}>{f}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </>
            ) : null
          }
        >
          <p>{AVISO_PDF_MULTISITIO}</p>
          {editable && total < SITIOS_MAX && (
            <div className="franja-sitios__agregar">
              <input
                className="campo__control franja-sitios__nombre"
                aria-label="Nombre de otra sucursal"
                placeholder="Otra sucursal (p. ej. Sucursal Sur)"
                maxLength={40}
                value={nombre}
                onChange={(e) => {
                  setNombre(e.target.value);
                  // Al escribir el nombre, el rojo de «escribe el nombre» ya no aplica.
                  if (error instanceof Error && error.message.startsWith('Para agregar otra sucursal')) setError(null);
                }}
              />
              <CampoSelect
                etiqueta="Tipo de la sucursal nueva"
                value={tipoNuevo ?? ''}
                opciones={[{ valor: '', texto: 'Igual que esta sucursal' }, ...TIPOS_SITIO.map((t) => ({ valor: t, texto: ETIQUETA_TIPO_SITIO[t] }))]}
                onChange={(e) => {
                  setTipoNuevo((e.target.value || null) as TipoSitio | null);
                  setTarifaNueva('no_se');
                }}
              />
              {tipoNuevo && <CampoSelect etiqueta="Tarifa de la sucursal nueva" value={tarifaNueva} opciones={opcionesTarifa(tipoNuevo, tarifaNueva)} onChange={(e) => setTarifaNueva(e.target.value as Tarifa | 'no_se')} />}
              <Boton chico variante="secundario" icono={<Plus aria-hidden />} cargando={creando} onClick={() => void agregar()}>
                Agregar sucursal
              </Boton>
            </div>
          )}
        </SitiosDelProyecto>
        <ErrorEnLinea error={errorSitios} alCerrar={() => setErrorSitios(null)} />
        <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      </div>
    </div>
  );
}
